import { Trash2, Copy, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Field } from "./Field";

const shareLink = (c) => {
  const origin = window.location.origin;
  if (c.scope === "membership") return `${origin}/membership?code=${encodeURIComponent(c.code)}`;
  const path = c.scope === "merch" ? "/shop" : "/classes";
  return `${origin}${path}?coupon=${encodeURIComponent(c.code)}`;
};

export default function AdminCoupons({ ctx }) {
  const { coupons, newCoupon, setNewCoupon, saveCoupon, toggleCoupon, delCoupon } = ctx;
  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied`);
  };
  return (
    <div className="mt-10 grid lg:grid-cols-2 gap-8" data-testid="admin-coupons">
      <div className="p-6 rounded-2xl bg-white border border-stone-200">
        <h3 className="font-serif font-black text-2xl text-[#121212] mb-1">Create a coupon</h3>
        <p className="text-sm text-stone-500 mb-4">Choose what each code applies to. A Membership code grants free membership months instead of a discount.</p>
        <div className="space-y-3">
          <Field label="Code" value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })} placeholder="LAUNCH20" />
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-stone-500 block mb-1">Applies to</label>
            <select data-testid="coupon-scope" value={newCoupon.scope || "both"} onChange={(e) => setNewCoupon({ ...newCoupon, scope: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white focus:border-[#121212] focus:outline-none">
              <option value="both">Classes &amp; camps + Merchandise</option>
              <option value="classes">Classes &amp; camps only</option>
              <option value="merch">Merchandise only</option>
              <option value="membership">Membership — free months</option>
            </select>
          </div>
          {newCoupon.scope === "membership" ? (
            <div>
              <Field label="Free months granted" type="number" value={newCoupon.membership_months} onChange={(e) => setNewCoupon({ ...newCoupon, membership_months: e.target.value })} />
              <p className="text-xs text-stone-500 mt-1">Gives full member perks free for this many months (no card needed). For existing paying members, it skips their next {newCoupon.membership_months || 1} charge{(newCoupon.membership_months || 1) > 1 ? "s" : ""}.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-stone-500 block mb-1">Type</label>
                <select data-testid="coupon-type" value={newCoupon.discount_type} onChange={(e) => setNewCoupon({ ...newCoupon, discount_type: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white focus:border-[#121212] focus:outline-none">
                  <option value="percent">Percentage (%)</option>
                  <option value="fixed">Fixed amount ($)</option>
                </select>
              </div>
              <Field label={newCoupon.discount_type === "percent" ? "Percent off" : "Dollars off"} type="number" value={newCoupon.discount_value} onChange={(e) => setNewCoupon({ ...newCoupon, discount_value: e.target.value })} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Max uses (blank = unlimited)" type="number" value={newCoupon.max_uses} onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: e.target.value })} placeholder="Set 1 for single-use" />
            <Field label="Expires on (optional)" type="date" value={newCoupon.expires_at} onChange={(e) => setNewCoupon({ ...newCoupon, expires_at: e.target.value })} />
          </div>
        </div>
        <button data-testid="save-coupon-btn" onClick={saveCoupon} className="mt-4 px-6 py-3 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold transition-all">Create coupon</button>
      </div>
      <div className="space-y-2">
        {coupons.length === 0 ? <p className="text-stone-500">No coupons yet.</p> :
          coupons.map((c) => {
            const expired = c.expires_at && new Date(c.expires_at) < new Date();
            const maxed = c.max_uses != null && c.uses >= c.max_uses;
            return (
              <div key={c.id} data-testid={`admin-coupon-${c.id}`} className="p-4 bg-white border border-stone-200 rounded-xl flex justify-between gap-3 items-center">
                <div className="min-w-0">
                  <p className="font-mono font-bold text-[#121212] flex items-center gap-2 flex-wrap">{c.code}
                    <span className="text-stone-400 font-sans font-normal text-sm">· {c.scope === "membership" ? `${c.membership_months || 1} free month${(c.membership_months || 1) > 1 ? "s" : ""}` : c.discount_type === "percent" ? `${c.discount_value}% off` : `$${c.discount_value} off`}</span>
                    <span className="px-2 py-0.5 text-[10px] tracking-[0.12em] uppercase font-bold rounded bg-stone-100 text-stone-600">{c.scope === "classes" ? "Classes & camps" : c.scope === "merch" ? "Merchandise" : c.scope === "membership" ? "Membership" : "All purchases"}</span>
                  </p>
                  <p className="text-xs text-stone-500 mt-0.5">
                    Used {c.uses}{c.max_uses != null ? `/${c.max_uses}` : ""}
                    {c.expires_at ? ` · expires ${new Date(c.expires_at).toLocaleDateString()}` : ""}
                    {expired ? " · EXPIRED" : maxed ? " · LIMIT REACHED" : ""}
                  </p>
                  <p data-testid={`coupon-stats-${c.id}`} className="text-xs mt-1 inline-flex items-center gap-2 font-medium text-[#121212]">
                    <span className="px-2 py-0.5 rounded bg-[#C6FF33]/40">{c.paid_redemptions || 0} paid {(c.paid_redemptions || 0) === 1 ? "use" : "uses"}</span>
                    <span className="px-2 py-0.5 rounded bg-stone-100">${(c.revenue_discounted || 0).toFixed(2)} discounted</span>
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`copy-coupon-code-${c.id}`} title="Copy code" onClick={() => copy(c.code, "Code")} className="p-2 text-stone-500 hover:text-[#121212]"><Copy size={18} /></button>
                  <button data-testid={`copy-coupon-link-${c.id}`} title="Copy shareable link" onClick={() => copy(shareLink(c), "Shareable link")} className="p-2 text-stone-500 hover:text-[#121212]"><Link2 size={18} /></button>
                  <button data-testid={`toggle-coupon-${c.id}`} onClick={() => toggleCoupon(c)} className={`px-3 py-2 text-xs tracking-[0.15em] uppercase font-bold border-2 border-[#121212] ${c.active ? "bg-[#C6FF33]" : "bg-stone-100 text-stone-500"}`}>{c.active ? "Active" : "Paused"}</button>
                  <button data-testid={`del-coupon-${c.id}`} onClick={() => delCoupon(c.id)} className="p-2 text-stone-500 hover:text-red-500"><Trash2 size={18} /></button>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
